import { useMemo } from 'react'

import { selectLiveStatus, useWindow } from '@krp/client-runtime'
import {
  aqsiDeliveryOperations,
  aqsiDeliveryReconciliation,
  aqsiDeliveryStatus,
  deliveryReconcileRequested,
  deliveryReloadRequested,
  DeliveryScreen,
  filterChosen,
  makeSelectDeliveryVm,
  selectOperationFilter,
  selectReconcileState,
  type DeliveryActions,
} from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'

/**
 * Пока есть операции в пути, статус перечитывается сам. На живом канале (LIVE)
 * его перечитывает событие службы `aqsi.delivery.changed`, а опрос остаётся
 * редким страховочным: событие может потеряться. Без канала — прежний опрос.
 */
const DELIVERY_POLL_MS = 10_000
const DELIVERY_SAFETY_POLL_MS = 60_000

/** Binding окна «Доставка каталога» ресурса AQSI (права `delivery.read`/`delivery.reconcile` проверяет сервер). */
export function DeliveryBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const skip = resourceId === ''
  const args = useMemo(() => ({ resourceId }), [resourceId])

  const statusSelect = useMemo(() => aqsiDeliveryStatus.select(args), [args])
  const polling = useAppSelector((state: RootState) => {
    const status = statusSelect(state).data
    return status !== undefined && status.pending + status.unknown > 0
  })
  const live = useAppSelector((state: RootState) => selectLiveStatus(state.live) === 'online')
  const pollingInterval = polling ? (live ? DELIVERY_SAFETY_POLL_MS : DELIVERY_POLL_MS) : 0
  aqsiDeliveryStatus.useQuerySubscription(args, { skip, pollingInterval })
  aqsiDeliveryOperations.useQuerySubscription(args, { skip, pollingInterval })
  aqsiDeliveryReconciliation.useQuerySubscription(args, { skip })

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectDeliveryVm()
    const selectOperations = aqsiDeliveryOperations.select(args)
    const selectReconciliation = aqsiDeliveryReconciliation.select(args)
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        status: statusSelect(state),
        operations: selectOperations(state),
        reconciliation: selectReconciliation(state),
        filter: selectOperationFilter(state.aqsi.delivery, resourceId),
        reconcile: selectReconcileState(state.aqsi.delivery, resourceId),
      })
  }, [args, statusSelect, resourceId])
  const vm = useAppSelector(selectVm)

  const actions = useMemo<DeliveryActions>(
    () => ({
      reload: () => dispatch(deliveryReloadRequested({ resourceId })),
      filterChanged: (filter) => dispatch(filterChosen({ resourceId, filter })),
      reconcile: () => dispatch(deliveryReconcileRequested({ resourceId })),
    }),
    [dispatch, resourceId],
  )

  return <DeliveryScreen vm={vm} actions={actions} />
}
